/**
 * Web Vitals Monitoring
 *
 * This module helps track Core Web Vitals metrics:
 * - LCP (Largest Contentful Paint): measures loading performance
 * - FID (First Input Delay): measures interactivity
 * - CLS (Cumulative Layout Shift): measures visual stability
 */

import { type Metric, onCLS, onFCP, onFID, onLCP, onTTFB } from "web-vitals";
import { env } from "@/lib/env";

type VitalsRating = "good" | "needs-improvement" | "poor";

interface VitalsReport {
  name: string;
  value: number;
  rating: VitalsRating;
  delta: number;
  id: string;
}

function getRating(name: string, value: number): VitalsRating {
  if (name === "LCP") {
    return value < 2500 ? "good" : value < 4000 ? "needs-improvement" : "poor";
  }
  if (name === "FID") {
    return value < 100 ? "good" : value < 300 ? "needs-improvement" : "poor";
  }
  if (name === "CLS") {
    return value < 0.1 ? "good" : value < 0.25 ? "needs-improvement" : "poor";
  }
  if (name === "TTFB") {
    return value < 600 ? "good" : value < 1000 ? "needs-improvement" : "poor";
  }
  if (name === "FCP") {
    return value < 1800 ? "good" : value < 3000 ? "needs-improvement" : "poor";
  }
  return "poor";
}

function reportWebVitals(metric: Metric): void {
  const { name, value, delta, id } = metric;
  const rating = getRating(name, value);

  const vitalsReport: VitalsReport = {
    name,
    value,
    rating,
    delta,
    id,
  };

  if (env.NODE_ENV === "development") {
    console.log(`Web Vital: ${name}`, vitalsReport);
  }

  if (env.NODE_ENV === "production") {
    if (window.umami) {
      window.umami.track("web-vitals", {
        metric: name,
        value: Math.round(name === "CLS" ? value * 1000 : value),
        rating,
      });
    }

    if (window.gtag) {
      window.gtag("event", "web_vitals", {
        event_category: "Web Vitals",
        event_label: id,
        value: Math.round(name === "CLS" ? value * 1000 : value),
        metric_id: id,
        metric_name: name,
        metric_rating: rating,
      });
    }
  }
}

export function initWebVitals(): void {
  onLCP(reportWebVitals);
  onFID(reportWebVitals);
  onCLS(reportWebVitals);
  onTTFB(reportWebVitals);
  onFCP(reportWebVitals);
}
